import { booleanAttr, emojiCharacter, rawAttr, stringAttr } from "@wiki/core";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import type {
  EditorView,
  NodeView,
  NodeViewConstructor,
  ViewMutationRecord,
} from "prosemirror-view";

import { setTaskChecked } from "../commands/lists";
import type { EditorContext } from "../context";
import { CodeBlockView, MathView, NoticeView, RuleView, ToggleView } from "./blocks";
import { AttachmentView, EmbedView, ImageView, VideoView } from "./media";

/**
 * A task checkbox is clickable — including in read mode — for any user who may
 * edit the document (`editor.md` §14). A reader without edit rights sees it
 * rendered and disabled.
 */
export class CheckboxItemView implements NodeView {
  readonly dom: HTMLElement;
  readonly contentDOM: HTMLElement;
  private node: ProseMirrorNode;
  private readonly input: HTMLInputElement;

  constructor(
    node: ProseMirrorNode,
    private readonly view: EditorView,
    private readonly getPos: () => number | undefined,
    private readonly context: EditorContext,
  ) {
    this.node = node;
    this.dom = document.createElement("li");
    this.dom.className = "wiki-task";
    this.input = document.createElement("input");
    this.input.type = "checkbox";
    this.input.className = "wiki-task__checkbox";
    this.input.setAttribute("contenteditable", "false");
    this.input.addEventListener("change", () => {
      const pos = this.getPos();
      if (pos === undefined || !this.clickable()) {
        this.input.checked = booleanAttr(this.node, "checked");
        return;
      }
      setTaskChecked(pos, this.input.checked)(this.view.state, this.view.dispatch, this.view);
    });
    this.contentDOM = document.createElement("div");
    this.contentDOM.className = "wiki-task__content";
    this.dom.append(this.input, this.contentDOM);
    this.render();
  }

  private clickable(): boolean {
    return !this.context.readOnly || this.context.canToggleTasks;
  }

  private render(): void {
    const checked = booleanAttr(this.node, "checked");
    this.input.checked = checked;
    this.input.disabled = !this.clickable();
    this.dom.setAttribute("data-checked", checked ? "true" : "false");
    this.dom.classList.toggle("wiki-task--checked", checked);
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  stopEvent(event: Event): boolean {
    return event.target === this.input;
  }

  ignoreMutation(mutation: ViewMutationRecord): boolean {
    return !this.contentDOM.contains(mutation.target);
  }
}

/** A mention chip. Document and collection mentions navigate when clicked. */
export class MentionView implements NodeView {
  readonly dom: HTMLElement;

  constructor(node: ProseMirrorNode, context: EditorContext) {
    const type = stringAttr(node, "type") ?? "user";
    this.dom = document.createElement("span");
    this.dom.className = `wiki-mention wiki-mention--${type}`;
    this.dom.setAttribute("data-mention-type", type);
    this.dom.setAttribute("data-model-id", stringAttr(node, "modelId") ?? "");
    this.dom.setAttribute("data-mention-id", stringAttr(node, "id") ?? "");
    this.dom.textContent = stringAttr(node, "label") ?? "";
    this.dom.setAttribute("contenteditable", "false");
    const href = rawAttr(node, "href");
    const target =
      typeof href === "string" && href !== ""
        ? href
        : type === "document"
          ? `/doc/${stringAttr(node, "modelId") ?? ""}`
          : type === "collection"
            ? `/collection/${stringAttr(node, "modelId") ?? ""}`
            : null;
    if (target !== null) {
      this.dom.setAttribute("data-href", target);
      this.dom.addEventListener("click", (event) => {
        event.preventDefault();
        context.openLink(target, event);
      });
    }
  }

  stopEvent(): boolean {
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }
}

export class EmojiView implements NodeView {
  readonly dom: HTMLElement;

  constructor(node: ProseMirrorNode) {
    const name = stringAttr(node, "data-name") ?? "";
    this.dom = document.createElement("span");
    this.dom.className = "wiki-emoji";
    this.dom.setAttribute("data-emoji", name);
    this.dom.setAttribute("contenteditable", "false");
    // An unknown shortcode is never an emoji node, but a custom workspace
    // emoji (a uuid) has no character; both fall back to the shortcode.
    this.dom.textContent = emojiCharacter(name) ?? `:${name}:`;
    this.dom.title = `:${name}:`;
  }

  stopEvent(): boolean {
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }
}

/** Every node view the editor installs, keyed by node name. */
export function buildNodeViews(context: EditorContext): Record<string, NodeViewConstructor> {
  return {
    image: (node, view, getPos) => new ImageView(node, view, getPos, context),
    video: (node) => new VideoView(node),
    attachment: (node, view, getPos) => new AttachmentView(node, view, getPos, context),
    embed: (node) => new EmbedView(node, context),
    code_block: (node, view, getPos) => new CodeBlockView(node, view, getPos, context),
    container_notice: (node, view, getPos) => new NoticeView(node, view, getPos, context),
    container_toggle: (node) => new ToggleView(node, context),
    math_block: (node, view, getPos) => new MathView(node, view, getPos, context, true),
    math_inline: (node, view, getPos) => new MathView(node, view, getPos, context, false),
    hr: (node, view, getPos) => new RuleView(node, view, getPos, context),
    checkbox_item: (node, view, getPos) => new CheckboxItemView(node, view, getPos, context),
    mention: (node) => new MentionView(node, context),
    emoji: (node) => new EmojiView(node),
  };
}

export { CodeBlockView, MathView, NoticeView, RuleView, ToggleView };
export { AttachmentView, EmbedView, ImageView, VideoView };
